












































































































































import { mapGetters } from 'vuex';
import {
  TIMEZONES,
  autoclickerJobStatusMap,
  TAutoClicker
} from '@/views/CopyStake/CopyStakeAutoclicker/types';
import { formatTimeDuration } from '@/helpers/timeDurationHelper';
import formatDate from '@/helpers/formatDate';
import {
  getAutoclickerGames,
  getAutoclickerJobDetails,
  getAutoclickerTemplates,
  getAutoclickerVoicesList,
  getAutoclickerAvatarsList
} from '@/api/CopyStake/AutoClicker';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import {
  EAutoclickerJobStatus,
  IAutoclickerJobDetailsRecord,
  TAutoclickerAvatarRecord,
  TAutoclickerGameData,
  TAutoclickerTemplate,
  TAutoclickerVoiceRecord
} from '@/api/schema';
import { NO_AVATAR_ID } from '../Autoclicker/CopyStakeAutoclickerEditModal.vue';

type TJobDetailsState = {
  statusMap: Record<EAutoclickerJobStatus, { label: string; type: string }>;
  jobDetails: IAutoclickerJobDetailsRecord | null;
  initialAutoclickerDetails: TAutoClicker | null;
  supportedGames: TAutoclickerGameData[];
  autoclickerTemplates: TAutoclickerTemplate[];
  avatarsList: TAutoclickerAvatarRecord[];
  voicesList: TAutoclickerVoiceRecord[];
  loading: Record<string, boolean>;
};

const VStatus = (): Promise<any> => import('@/components/VStatus.vue');

export default {
  name: 'CopyStakeAutoclickerJobDetails',
  components: {
    VStatus
  },

  data(): TJobDetailsState {
    return {
      statusMap: autoclickerJobStatusMap,
      jobDetails: null,
      initialAutoclickerDetails: null,
      supportedGames: [],
      autoclickerTemplates: [],
      avatarsList: [],
      voicesList: [],
      loading: {
        jobDetails: false,
        initialAutoclickerDetails: false,
        supportedGames: false,
        autoclickerTemplates: false,
        avatars: false,
        voices: false
      }
    };
  },
  watch: {
    requestParams: {
      async handler(): Promise<void> {
        await this.fetchJobDetailsData();
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    requestParams(): any {
      return {
        jobId: this.jobId,
        operatorId: this.operatorId
      };
    },
    jobId(): number | null {
      return +this.$route.params?.jobId || null;
    },
    timezonesMap(): Record<string, string> {
      return TIMEZONES.reduce((all, curr) => {
        all[curr.value] = curr.text.split(' ')[0];

        return all;
      }, {});
    },
    isLoading(): boolean {
      return Object.values(this.loading).some(Boolean);
    }
  },
  methods: {
    formatTimeDuration,
    convertDate(date: string, zone: string): string {
      return `${formatDate(date, 'yyyy-MM-dd HH:mm', false, false)} ${
        this.timezonesMap[zone]
      }`;
    },
    formatAvatar(avatarId: string): string {
      const found = this.avatarsList?.find(
        (avatar: TAutoclickerAvatarRecord) =>
          avatar.heygenAvatarId === avatarId
      );
      return found?.avatarName || '-';
    },
    formatVoice(data: TAutoClicker): string {
      const isNoAvatar = data.heygenAvatarId === NO_AVATAR_ID;

      const found = this.voicesList?.find((voice: TAutoclickerVoiceRecord) =>
        isNoAvatar
          ? voice.elevenlabVoiceId === data.heygenVoiceId
          : voice.heygenVoiceId === data.heygenVoiceId
      );

      return found
        ? `${found.voiceName} (${found.gender}) (${
            isNoAvatar ? found.elevenlabVoiceId : found.heygenVoiceId
          })`
        : '-';
    },
    formatSupportedGames(games: number[]): string {
      const names = games
        .map((gameId) => {
          const game = this.supportedGames?.find(({ id }) => id === gameId);
          return game?.gameName;
        })
        .filter(Boolean);

      if (names.length === 0) return '-';

      return names.length === 1 ? names[0] : names.join(', ');
    },
    formatSupportedTemplate(templateId: number): string {
      const found = this.autoclickerTemplates?.find(
        (t) => t.id === templateId
      );
      return found?.name || '-';
    },
    async fetchJobDetailsData(): Promise<void> {
      if (!this.operatorId || !this.jobId) return;

      try {
        this.loading.jobDetails = true;
        this.loading.initialAutoclickerDetails = true;

        const jobData = await getAutoclickerJobDetails(
          this.requestParams.jobId
        );

        this.jobDetails = jobData || null;

        this.parseAutoclickerConfigDetails(jobData);
      } catch (error) {
        errorToastMessage(error);

        this.jobDetails = null;
      } finally {
        this.loading.jobDetails = false;
      }
    },
    async parseAutoclickerConfigDetails(
      jobData: IAutoclickerJobDetailsRecord
    ): Promise<void> {
      const initialConfigJson = jobData?.initialConfig || null;
      if (!initialConfigJson) return;

      try {
        this.loading.initialAutoclickerDetails = true;

        const parsedAutoclickerDetails = initialConfigJson
          ? JSON.parse(initialConfigJson)
          : null;

        this.initialAutoclickerDetails = parsedAutoclickerDetails || null;

        this.fetchAutoclickerSupportedGames();
        this.fetchAutoclickerTemplates();
        this.fetchAutoclickerAvatars();
        this.fetchAutoclickerVoices();
      } catch (error) {
        errorToastMessage(error);

        this.initialAutoclickerDetails = null;
      } finally {
        this.loading.initialAutoclickerDetails = false;
      }
    },
    async fetchAutoclickerSupportedGames(): Promise<void> {
      if (!this.jobDetails.autoclickerConfigId) return;

      try {
        this.loading.supportedGames = true;

        const gamesData = await getAutoclickerGames();

        this.supportedGames = gamesData || [];
      } catch (error) {
        errorToastMessage(error);

        this.supportedGames = [];
      } finally {
        this.loading.supportedGames = false;
      }
    },
    async fetchAutoclickerTemplates(): Promise<void> {
      if (!this.jobDetails.autoclickerConfigId) return;

      try {
        this.loading.autoclickerTemplates = true;

        const templatesData = await getAutoclickerTemplates();

        this.autoclickerTemplates = templatesData || [];
      } catch (error) {
        errorToastMessage(error);

        this.autoclickerTemplates = [];
      } finally {
        this.loading.autoclickerTemplates = false;
      }
    },
    async fetchAutoclickerAvatars(): Promise<void> {
      if (!this.jobDetails.autoclickerConfigId) return;

      try {
        this.loading.avatars = true;

        const avatarsData = await getAutoclickerAvatarsList();

        this.avatarsList = avatarsData || [];
      } catch (error) {
        errorToastMessage(error);

        this.avatarsList = [];
      } finally {
        this.loading.avatars = false;
      }
    },
    async fetchAutoclickerVoices(): Promise<void> {
      if (!this.jobDetails.autoclickerConfigId) return;

      try {
        this.loading.voices = true;

        const voicesData = await getAutoclickerVoicesList();

        this.voicesList = voicesData || [];
      } catch (error) {
        errorToastMessage(error);

        this.voicesList = [];
      } finally {
        this.loading.voices = false;
      }
    }
  }
};
